import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import GLOBALS from '../config/globals';
import { getAutoManagerConfig } from '../helpers/autoManagerHelpers';
import { translateEvent, translatePitchRoll, buildTickerText, translateOuts } from '../utils/gameUtils';
import { makeRunner, deriveResultState } from '../helpers/gamePlayHelpers';
import { cleanTeamName } from '../helpers/teamHelpers';

import Card from './Card';
import RunnersDisplay from './RunnersDisplay';
import GameResultPopup from './GameResultPopup';
import DiceAnimation from './DiceAnimation';
import PlayByPlayReveal from './PlayByPlayReveal';

const cpuRobotOverlay = <FontAwesomeIcon icon={faRobot} className="cpu-robot-overlay" aria-label="CPU-controlled opponent" />;

const parseAspectRatio = (ratioStr) => {
    if (!ratioStr) return undefined;
    const [w, h] = ratioStr.split(':').map(Number);
    return w && h ? `${w} / ${h}` : undefined;
};

const buildStadiumColorVars = (visualConfig) => {
    const vars = {};
    if (visualConfig.scoreboard?.backgroundColor) vars['--stadium-scoreboard-bg'] = visualConfig.scoreboard.backgroundColor;
    if (visualConfig.scoreboard?.textColor) vars['--stadium-scoreboard-text'] = visualConfig.scoreboard.textColor;
    if (visualConfig.scoreboard?.borderColor) vars['--stadium-scoreboard-border'] = visualConfig.scoreboard.borderColor;
    if (visualConfig.scoreboard?.accentColor) vars['--stadium-scoreboard-accent'] = visualConfig.scoreboard.accentColor;
    if (visualConfig.resultPanel?.backgroundColor) vars['--stadium-result-bg'] = visualConfig.resultPanel.backgroundColor;
    if (visualConfig.resultPanel?.textColor) vars['--stadium-result-text'] = visualConfig.resultPanel.textColor;
    if (visualConfig.resultPanel?.borderColor) vars['--stadium-result-border'] = visualConfig.resultPanel.borderColor;
    if (visualConfig.buttons?.primary) vars['--stadium-btn-primary'] = visualConfig.buttons.primary;
    if (visualConfig.buttons?.primaryText) vars['--stadium-btn-primary-text'] = visualConfig.buttons.primaryText;
    return vars;
};

// Every component position is nested as { box: { top, left, width, height? }, ... } and
// every value is a percent string. boxStyle() reads the placement (% of the canvas).
// cqw() converts a "%-of-canvas-width" string into a cqw length so fonts and sizes scale
// with the canvas — the CSS twin of how iOS scales everything by GeometryReader width.
const boxStyle = (config) => {
    const b = config?.box;
    if (!b) return undefined;
    const style = { top: b.top, left: b.left, width: b.width };
    if (b.height) style.height = b.height;
    return style;
};

const cqw = (pct) => (pct ? `${parseFloat(pct)}cqw` : undefined);

const StadiumGameLayout = ({
    game, participants, gameSpeed, stadium,
    engine, pitcherFatigue,
    autoPlay, managerMode,
    isManagerOpen, setIsManagerOpen,
    handleRollClick, toggleManager, subMenu, handleAutoCycle,
}) => {
    const { revealPhase, userIsPitching, displayUserIsPitching, currentBatterCard, currentPitcherCard,
        rolling, managedEventPopup, eventUserIsPitchingRef, displayEvent, inningOverPopup } = engine;
    const { currentPitcherEffectiveStamina } = pitcherFatigue;

    const visualConfig = stadium.visualConfig;
    const components = visualConfig.components || {};

    const fontsKey = visualConfig.fonts?.join(',') ?? '';
    useEffect(() => {
        if (!fontsKey) return;
        const families = fontsKey.split(',').map(f => f.trim().replace(/ /g, '+')).join('&family=');
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
        document.head.appendChild(link);
        return () => document.head.removeChild(link);
    }, [fontsKey]);

    const latestEvent = game.GameEvents?.[0] || {};
    const cpuVariation = game.specialGameType
        ? { name: game.specialGameType.toLowerCase(), cssClass: `variation-${game.specialGameType.toLowerCase()}` }
        : null;
    const homeParticipant = participants?.find(p => p.role === 'home');
    const awayParticipant = participants?.find(p => p.role === 'away');

    const nextPitcherWork = game.nextPitcherWork ?? 0;
    const buttonLabel = userIsPitching ? 'Hurl' : 'Swing';

    const runnerSource = displayEvent || latestEvent;
    const firstRunner = makeRunner(runnerSource.firstBaseRunnerLineup, runnerSource.firstBaseRunnerLineupID, displayUserIsPitching);
    const secondRunner = makeRunner(runnerSource.secondBaseRunnerLineup, runnerSource.secondBaseRunnerLineupID, displayUserIsPitching);
    const thirdRunner = makeRunner(runnerSource.thirdBaseRunnerLineup, runnerSource.thirdBaseRunnerLineupID, displayUserIsPitching);

    const {
        strikeThroughH, strikeThroughP, runnersOnBase, showResultPopup,
        resultOnPitcherCard, resultIsGoodForUser, resultIsXBH,
        resultPopupText, eventWasModified, origPopupText,
    } = deriveResultState(revealPhase, latestEvent, userIsPitching, eventUserIsPitchingRef);

    const showSteal = !userIsPitching && runnersOnBase;
    const outsInHalf = (latestEvent.ipOuts ?? 0) % 6 % 3;
    const showBunt = !userIsPitching && runnersOnBase && outsInHalf < 2;

    const ipOuts = runnerSource.ipOuts ?? 0;
    const { inning, inningHalf } = translateOuts(ipOuts, !!game.isFinal);
    const halfArrow = inningHalf === 'Top' ? '▲' : '▼';
    const inningLabel = game.isFinal ? 'Final' : `${halfArrow} ${inning}`;

    const awayTeam = cleanTeamName(awayParticipant?.UserTeam?.name) ?? 'Away';
    const homeTeam = cleanTeamName(homeParticipant?.UserTeam?.name) ?? 'Home';
    const awayR = runnerSource.visitingScore ?? game.scoreboardData?.visitorScore ?? 0;
    const awayH = game.scoreboardData?.visitorHits ?? 0;
    const awayE = game.scoreboardData?.visitorErrors ?? 0;
    const homeR = runnerSource.homeScore ?? game.scoreboardData?.homeScore ?? 0;
    const homeH = game.scoreboardData?.homeHits ?? 0;
    const homeE = game.scoreboardData?.homeErrors ?? 0;

    const stadiumColorVars = buildStadiumColorVars(visualConfig);
    const backgroundImage = visualConfig.backgroundImage?.mobile;

    const pageBackground = visualConfig.pageBackground;
    let pageBackgroundStyle = {};
    if (typeof pageBackground === 'object' && pageBackground?.top) {
        const imgRatio = pageBackground.imageRatio || 1.676;
        const gradientHeight = `min(${Math.round(430 * imgRatio)}px, calc(100vw * ${imgRatio}))`;
        pageBackgroundStyle = {
            backgroundColor: pageBackground.bottom,
            backgroundImage: `linear-gradient(to bottom, ${pageBackground.top}, ${pageBackground.bottom})`,
            backgroundSize: `100% ${gradientHeight}`,
            backgroundRepeat: 'no-repeat',
        };
    } else if (pageBackground) {
        pageBackgroundStyle = { backgroundColor: pageBackground };
    }

    return (
        <div className="play-games-page stadium-layout" style={{
            ...stadiumColorVars,
            ...pageBackgroundStyle,
            ...(visualConfig.fontFamily && { fontFamily: visualConfig.fontFamily }),
        }}>
            <div
                className="stadium-canvas"
                style={{
                    aspectRatio: parseAspectRatio(visualConfig.aspectRatio),
                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                }}
            >
                {components.scoreboard && (() => {
                    const sb = components.scoreboard;
                    const col = sb.columns || {};
                    const rows = sb.rows || {};
                    const font = sb.font || {};
                    const valWidth = sb.value?.width;
                    // color = header text on the cream banner (inning, out dots).
                    // rowColor = team-row text on the teal background (names + R/H/E values).
                    const color = sb.color;
                    const rowColor = sb.rowColor ?? sb.color;
                    // A live row of cells (name + R/H/E values) anchored to the shared columns,
                    // vertically centered on its row line. Away and home read the SAME column
                    // percentages, so the value columns can never drift out of alignment.
                    const scoreRow = (name, r, h, e, top) => (
                        <>
                            <span className="sb-name" style={{ left: col.name, top, width: `calc(${col.r} - ${col.name} - ${valWidth})`, fontSize: cqw(font.name), color: rowColor }}>{name}</span>
                            <span className="sb-val" style={{ left: col.r, top, width: valWidth, fontSize: cqw(font.value), color: rowColor }}>{r}</span>
                            <span className="sb-val" style={{ left: col.h, top, width: valWidth, fontSize: cqw(font.value), color: rowColor }}>{h}</span>
                            <span className="sb-val" style={{ left: col.e, top, width: valWidth, fontSize: cqw(font.value), color: rowColor }}>{e}</span>
                        </>
                    );
                    return (
                        <div className="stadium-component stadium-scoreboard" style={boxStyle(sb)}>
                            <span className="sb-inning" style={{ left: col.inning ?? col.name, top: rows.header, width: `calc(${col.r} - ${col.inning ?? col.name})`, fontSize: cqw(font.inning), color }}>{inningLabel}</span>
                            {!game.isFinal && (
                                <span className="sb-outs" style={{ left: sb.outs?.left, top: rows.header, color: sb.outs?.color ?? color }}>
                                    {[0, 1].map(i => (
                                        <span key={i} className={`out-dot${i < outsInHalf ? ' out-dot-filled' : ''}`} />
                                    ))}
                                </span>
                            )}
                            {scoreRow(awayTeam, awayR, awayH, awayE, rows.away)}
                            {scoreRow(homeTeam, homeR, homeH, homeE, rows.home)}
                        </div>
                    );
                })()}

                {components.runners && (
                    <div className="stadium-component stadium-runners" style={{ ...boxStyle(components.runners), '--meeple-h': cqw(components.runners.meeple?.height) }}>
                        <RunnersDisplay
                            firstBase={firstRunner}
                            secondBase={secondRunner}
                            thirdBase={thirdRunner}
                            hideScene
                            meepleConfig={game.cosmetics?.meeple}
                            bases={components.runners.bases}
                        />
                    </div>
                )}

                {components.batterCard && currentBatterCard && (
                    <div className={`stadium-component stadium-batter-card${displayUserIsPitching ? ' cpu-side' : ''}`} style={boxStyle(components.batterCard)}>
                        <Card
                            card={currentBatterCard}
                            diceResultH={revealPhase && !['pending', 'done'].includes(revealPhase) ? latestEvent.diceResultH : null}
                            diceResultX={revealPhase && !['pending', 'done'].includes(revealPhase) ? latestEvent.diceResultX : null}
                            variation={displayUserIsPitching ? cpuVariation : null}
                            frontOverlay={displayUserIsPitching ? cpuRobotOverlay : null}
                        />
                    </div>
                )}

                {components.pitcherCard && currentPitcherCard && (
                    <div className={`stadium-component stadium-pitcher-card${!displayUserIsPitching ? ' cpu-side' : ''}${nextPitcherWork >= currentPitcherEffectiveStamina ? ' pitcher-fatigued' : ''}`} style={boxStyle(components.pitcherCard)}>
                        <Card
                            card={currentPitcherCard}
                            pitcherWork={nextPitcherWork}
                            diceResultP={revealPhase && !['pending', 'batting', 'done'].includes(revealPhase) ? latestEvent.diceResultP : null}
                            variation={displayUserIsPitching ? null : cpuVariation}
                            frontOverlay={!displayUserIsPitching ? cpuRobotOverlay : null}
                        />
                    </div>
                )}

                <div className="stadium-component stadium-result-popup" style={boxStyle(components.resultPopup)}>
                    <GameResultPopup
                        showResultPopup={showResultPopup}
                        resultOnPitcherCard={resultOnPitcherCard}
                        resultIsGoodForUser={resultIsGoodForUser}
                        resultIsXBH={resultIsXBH}
                        eventWasModified={eventWasModified}
                        origPopupText={origPopupText}
                        resultPopupText={resultPopupText}
                        managedEventPopup={managedEventPopup}
                    />
                </div>

                {inningOverPopup && (
                    <div className="inning-over-popup">Inning Over</div>
                )}

                {components.buttons && (
                    <div className="stadium-component stadium-buttons" style={boxStyle(components.buttons)}>
                        <div className="game-info">
                            {game.isFinal ? (
                                <span className="final-summary">
                                    Final: <Link to={`/game/${game.gameID}/summary`} className="button">Game Summary</Link>
                                </span>
                            ) : (
                                <>
                                    <button className="game-btn" onClick={toggleManager}>
                                        <span className="label-full">Calls</span><span className="label-short">Calls</span>
                                    </button>
                                    <button
                                        className={`game-btn${autoPlay ? ' game-btn--active' : ''}${managerMode ? ` ${GLOBALS.AUTO_MANAGER.CSS[managerMode]}` : ''}`}
                                        onClick={handleAutoCycle}
                                    >
                                        {!autoPlay && 'Auto'}
                                        {autoPlay && !managerMode && 'Auto'}
                                        {autoPlay && managerMode && (
                                            <>
                                                <span className="label-full">Auto: {getAutoManagerConfig(managerMode)?.label}</span>
                                                <span className="label-short">Auto: {getAutoManagerConfig(managerMode)?.mobileLabel}</span>
                                            </>
                                        )}
                                    </button>
                                    <button className="game-btn game-btn--cta" onClick={handleRollClick} disabled={rolling}>
                                        {rolling ? <><span className="label-full">Rolling...</span><span className="label-short">...</span></> : buttonLabel}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {components.dice && revealPhase && revealPhase !== 'pending' && (
                    <div className="stadium-component stadium-dice" style={boxStyle(components.dice)}>
                        <div className="result-dice-row">
                            <div className="result-dice-group">
                                <span className="result-dice-label"><span className="label-full">Batting</span><span className="label-short">B:</span></span>
                                <DiceAnimation
                                    diceResultH={latestEvent.diceResultX ? undefined : latestEvent.diceResultH}
                                    diceResultX={latestEvent.diceResultX}
                                    gameSpeed={gameSpeed}
                                    diceConfig={game.cosmetics?.dice}
                                />
                                <span className={`result-box${strikeThroughH ? ' strike-through' : ''}`}>
                                    <span className="label-full">{translateEvent(latestEvent.origResultType).event}</span>
                                    <span className="label-short">{translateEvent(latestEvent.origResultType).smallEvent}</span>
                                </span>
                            </div>
                            {revealPhase !== 'batting' && (
                                <div className="result-dice-group">
                                    <span className="result-dice-label"><span className="label-full">Pitching</span><span className="label-short">P:</span></span>
                                    <DiceAnimation
                                        diceResultP={latestEvent.diceResultP}
                                        gameSpeed={gameSpeed}
                                        diceConfig={game.cosmetics?.dice}
                                    />
                                    <span
                                        className={`result-box${strikeThroughP ? ' strike-through' : ''}${
                                            latestEvent?.diceModifiers?.pitcher?.includes('fatigue') &&
                                            latestEvent?.origDiceResults?.pitcher &&
                                            latestEvent?.diceResults?.pitcher &&
                                            latestEvent.origDiceResults.pitcher !== latestEvent.diceResults.pitcher
                                                ? ' fatigue-modified' : ''
                                        }`}
                                        title={
                                            latestEvent?.diceModifiers?.pitcher?.includes('fatigue') &&
                                            latestEvent?.origDiceResults?.pitcher &&
                                            latestEvent?.diceResults?.pitcher &&
                                            latestEvent.origDiceResults.pitcher !== latestEvent.diceResults.pitcher
                                                ? `Pitcher fatigued: ${latestEvent.origDiceResults.pitcher} → ${latestEvent.diceResults.pitcher}`
                                                : undefined
                                        }
                                    >
                                        <span className="label-full">{translatePitchRoll(latestEvent.pitchResultType).result}</span>
                                        <span className="label-short">{translatePitchRoll(latestEvent.pitchResultType).smallResult}</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {components.resultPanel && revealPhase && ['final', 'description', 'done'].includes(revealPhase) && (() => {
                    const tickerText = buildTickerText(latestEvent);
                    const tickerDisplay = `${tickerText}!`;
                    const tickerCount = Math.max(20, Math.ceil(80 / (tickerDisplay.length || 5)));
                    return (
                        <div className="stadium-component stadium-result-panel" style={boxStyle(components.resultPanel)}>
                            <div className="result-final-box">
                                {components.resultPanel.image && (
                                    <img src={components.resultPanel.image} alt="" className="result-panel-logo" />
                                )}
                                <div className="result-final-content">
                                    <div className="result-final-ticker">
                                        {Array.from({ length: tickerCount }, (_, i) => (
                                            <span
                                                key={i}
                                                className="result-final-label"
                                                style={{ animationDelay: `${i * 80}ms` }}
                                            >
                                                {tickerDisplay}{' '}
                                            </span>
                                        ))}
                                    </div>
                                    {['description', 'done'].includes(revealPhase) && (latestEvent.playByPlay?.length || latestEvent.resultDescription) && (
                                        <div className="result-description">
                                            <PlayByPlayReveal
                                                lines={latestEvent.playByPlay?.length ? latestEvent.playByPlay : latestEvent.resultDescription ? [latestEvent.resultDescription] : []}
                                                gameSpeed={gameSpeed}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {isManagerOpen && (
                <div className="accordion-content">
                    <h3>Play Calls</h3>
                    {userIsPitching ? (
                        <button onClick={engine.handleManagedWalk}>Intentional Walk</button>
                    ) : (
                        <button className="inactive">Intentional Walk</button>
                    )}
                    <button onClick={subMenu}>Substitute</button>

                    {showSteal ? (
                        <button onClick={() => { setIsManagerOpen(false); engine.setIsStealModalOpen(true); }}>Steal</button>
                    ) : (
                        <button className="inactive">Steal</button>
                    )}

                    {showBunt ? (
                        <button onClick={() => { setIsManagerOpen(false); engine.handleManagedBunt(); }}>Sacrifice Bunt</button>
                    ) : (
                        <button className="inactive">Sacrifice Bunt</button>
                    )}
                </div>
            )}
        </div>
    );
};

export default StadiumGameLayout;
